import { type MouseEvent, memo, type ReactNode, useCallback } from 'react';

import { CollapsibleCard } from '~/components/CollapsibleCard';

export type CollapsibleSectionProps = {
  title: string;
  children: ReactNode;
  onOpenChange?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  headerAction?: ReactNode;
  'data-testid'?: string;
};

const getExpansionKey = (isExpanded: boolean | undefined) => {
  if (isExpanded === undefined) {
    return 'unset';
  }

  return isExpanded ? 'expanded' : 'collapsed';
};

export const CollapsibleSection = memo(
  ({ title, children, onOpenChange, isExpanded, headerAction, 'data-testid': testId }: CollapsibleSectionProps) => {
    const expansionKey = getExpansionKey(isExpanded);
    const handleStopPropagation = useCallback((e: MouseEvent) => e.stopPropagation(), []);

    return (
      <div className="rounded-8 ads:bg-raised bg-gray-2" data-testid={testId} data-is-expanded={isExpanded}>
        <CollapsibleCard
          key={`${title}-${expansionKey}`}
          header={
            <div className="flex w-full items-center justify-between">
              <div className="font-semibold ads:text-primary text-12 text-gray-12">{title}</div>
              {headerAction && (
                // biome-ignore lint/a11y/noStaticElementInteractions: only stops the click from toggling the collapsible header, not itself interactive
                // biome-ignore lint/a11y/useKeyWithClickEvents: only stops the click from toggling the collapsible header, not itself interactive
                <div onClick={handleStopPropagation}>{headerAction}</div>
              )}
            </div>
          }
          variant="ghost"
          onOpenChange={onOpenChange}
          isDefaultExpanded={isExpanded}
          clickableHeader={true}
          className="p-0"
        >
          {children}
        </CollapsibleCard>
      </div>
    );
  },
);

CollapsibleSection.displayName = 'CollapsibleSection';
